// Best-effort device details for clock-in / clock-out.
//
// A browser cannot reveal the machine's hostname, so this is the most a web
// page can know: OS (with version), browser, and on Android the phone model.
// The server also reads the User-Agent header, so this only adds what the
// header cannot say -- the UA string has reported "Windows NT 10.0" for
// Windows 11 since 2021, and Chrome on Android hides the model behind "K".
//
// Like captureLocation, this never rejects: a browser without client hints
// (Firefox, Safari) resolves to the basics and the punch goes out regardless.

const HINT_TIMEOUT = 1500;

const windowsName = (platformVersion) => {
  // Client hints report Windows 11 as platformVersion 13.0.0 or higher.
  const major = parseInt(platformVersion, 10);
  if (!Number.isFinite(major)) return "Windows";
  return major >= 13 ? "Windows 11" : "Windows 10";
};

export const captureDevice = async () => {
  if (typeof navigator === "undefined") return null;

  const base = {
    mobile: /Mobi|Android/i.test(navigator.userAgent),
    screen:
      typeof window !== "undefined" && window.screen
        ? `${window.screen.width}x${window.screen.height}`
        : "",
  };

  const uaData = navigator.userAgentData;
  if (!uaData?.getHighEntropyValues) return base;

  try {
    const hints = await Promise.race([
      uaData.getHighEntropyValues(["platformVersion", "model", "fullVersionList"]),
      new Promise((resolve) => setTimeout(() => resolve(null), HINT_TIMEOUT)),
    ]);
    if (!hints) return base;

    const brand = (hints.fullVersionList || uaData.brands || []).find(
      (b) => !/Not.?A.?Brand|Chromium/i.test(b.brand)
    );
    const os =
      hints.platform === "Windows"
        ? windowsName(hints.platformVersion)
        : [hints.platform, hints.platformVersion?.split(".")[0]].filter(Boolean).join(" ");

    return {
      ...base,
      mobile: Boolean(uaData.mobile ?? base.mobile),
      os,
      browser: brand ? `${brand.brand} ${String(brand.version).split(".")[0]}` : "",
      model: hints.model || "",
    };
  } catch {
    return base;
  }
};

/** "Chrome 130 · Windows 11 · Desktop" -- the server sends this as `label`. */
export const formatDevice = (device) => device?.label || "";
